// WhatsApp Center — bulk broadcast platform mirroring EmailCenter.jsx.
// Tabs: Broadcast · Campaigns · Templates · Deliveries · Opt-out.
import { useEffect, useMemo, useRef, useState } from "react";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { toast } from "sonner";
import { useCompany } from "../../context/CompanyContext";
import { Whatsapp, Users, Broadcast, Trash, Play, Pause, Prohibit, Sparkle, ArrowClockwise, Paperclip, UploadSimple, FileCsv } from "@phosphor-icons/react";

const TABS = ["broadcast", "campaigns", "templates", "deliveries", "optout"];

export default function WhatsAppCenter() {
  const [tab, setTab] = useState("broadcast");
  const [templates, setTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [config, setConfig] = useState({ configured: false, sender: "", default_template: "" });
  const [activeCampaignId, setActiveCampaignId] = useState(null);

  const loadAll = async () => {
    try {
      const [t, c, cfg] = await Promise.all([
        api.get("/whatsapp/templates").catch(() => ({ data: { templates: [] } })),
        api.get("/whatsapp/campaigns").catch(() => ({ data: { campaigns: [] } })),
        api.get("/whatsapp/config").catch(() => ({ data: { configured: false } })),
      ]);
      setTemplates(t.data.templates || []);
      setCampaigns(c.data.campaigns || []);
      setConfig(cfg.data || {});
    } catch (e) { console.error(e); }
  };
  useEffect(() => { loadAll(); }, []);

  return (
    <Layout title="WhatsApp Center" subtitle="Bulk broadcasts · templates · delivery tracking">
      <div className="mb-6" data-testid="wa-center-page">
        {!config.configured && (
          <div className="mb-4 border-l-4 border-[#F59E0B] bg-[#FFF8ED] px-5 py-3 text-sm" data-testid="wa-not-configured">
            <b>Bhash not configured.</b> Add <code>BHASH_USER</code> + <code>BHASH_PASS</code> in <code>backend/.env</code> and restart. WhatsApp broadcasts require the Bhash SMS integration.
          </div>
        )}
        <div className="flex flex-wrap gap-1 border-b border-black/10">
          {TABS.map(t => (
            <button
              key={t}
              data-testid={`wa-tab-${t}`}
              onClick={() => setTab(t)}
              className={`px-4 py-2.5 text-xs font-mono uppercase tracking-wider font-bold border-b-2 -mb-px transition ${
                tab === t ? "border-[#25D366] text-[#0A0A0A]" : "border-transparent text-[#717680] hover:text-black"
              }`}
            >
              {t === "broadcast" ? "New Broadcast" : t === "optout" ? "Opt-out" : t}
            </button>
          ))}
        </div>
      </div>

      {tab === "broadcast" && (
        <BroadcastForm templates={templates} config={config} onSent={() => { loadAll(); setTab("campaigns"); }} />
      )}
      {tab === "campaigns" && (
        <CampaignsList campaigns={campaigns} onRefresh={loadAll} onOpen={(id) => { setActiveCampaignId(id); setTab("deliveries"); }} />
      )}
      {tab === "templates" && (
        <TemplatesTab templates={templates} onChange={loadAll} />
      )}
      {tab === "deliveries" && (
        <DeliveriesTab campaigns={campaigns} activeCampaignId={activeCampaignId} setActiveCampaignId={setActiveCampaignId} />
      )}
      {tab === "optout" && (
        <OptoutTab />
      )}
    </Layout>
  );
}

// -------- Broadcast tab --------
function BroadcastForm({ templates, config, onSent }) {
  const { activeId } = useCompany();
  const [name, setName] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [body, setBody] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");
  const [source, setSource] = useState("adhoc");
  const [csvText, setCsvText] = useState("");
  const [csvFileName, setCsvFileName] = useState("");
  const [saveAsContacts, setSaveAsContacts] = useState(false);
  const [leadStatus, setLeadStatus] = useState(["hot", "warm"]);
  const [ratePerMinute, setRatePerMinute] = useState(60);
  const [scheduledFor, setScheduledFor] = useState("");
  const [previews, setPreviews] = useState([]);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!templateId) return;
    const t = templates.find(t => t.id === templateId);
    if (t) { setBody(t.body || ""); if (!mediaUrl) setMediaUrl(t.media_url || ""); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, templates]);

  // Robust CSV parser: handles quoted commas, header detection, common column names.
  const parseCsv = () => {
    const text = (csvText || "").trim();
    if (!text) return [];
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (!lines.length) return [];

    // Detect header row — if first line contains any of these words, treat as header
    const HEADER_HINTS = /^(name|phone|mobile|email|company|address|gstin|remark|type|contact)/i;
    const splitRow = (line) => {
      const out = [];
      let cur = "", inQ = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"' && line[i+1] === '"') { cur += '"'; i++; }
        else if (ch === '"') inQ = !inQ;
        else if (ch === ',' && !inQ) { out.push(cur); cur = ""; }
        else cur += ch;
      }
      out.push(cur);
      return out.map(s => s.trim());
    };
    const first = splitRow(lines[0]);
    const hasHeader = first.some(c => HEADER_HINTS.test(c));
    let headers = hasHeader ? first.map(h => h.toLowerCase()) : ["phone", "name", "company", "email", "address", "gstin"];
    const dataLines = hasHeader ? lines.slice(1) : lines;

    const idxOf = (candidates) => {
      for (const c of candidates) {
        const i = headers.findIndex(h => h.replace(/[\s_-]/g, "").includes(c));
        if (i !== -1) return i;
      }
      return -1;
    };
    const iPhone   = idxOf(["phone", "mobile", "contact", "number"]);
    const iName    = idxOf(["name", "fullname", "customer"]);
    const iCompany = idxOf(["company", "organi", "firm", "hospital"]);
    const iEmail   = idxOf(["email"]);
    const iAddress = idxOf(["address", "city", "location"]);
    const iGstin   = idxOf(["gstin", "gst"]);

    const rows = [];
    for (const line of dataLines) {
      const cells = splitRow(line);
      const phone = (iPhone >= 0 ? cells[iPhone] : cells[0]) || "";
      const name  = (iName >= 0 ? cells[iName] : (cells[1] || "")) || "";
      const company = (iCompany >= 0 ? cells[iCompany] : (cells[2] || "")) || "";
      const email = iEmail >= 0 ? (cells[iEmail] || "") : "";
      const address = iAddress >= 0 ? (cells[iAddress] || "") : "";
      const gstin = iGstin >= 0 ? (cells[iGstin] || "") : "";
      if (!phone && !email && !name) continue;
      rows.push({
        phone: phone.trim(), name: name.trim(), company: company.trim(),
        email: email.trim(), address: address.trim(), gstin: gstin.trim(),
        vars: { name: name.trim(), company: company.trim() },
      });
    }
    return rows;
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) return toast.error("File too large (max 5 MB)");
    try {
      const text = await f.text();
      setCsvText(text);
      setCsvFileName(f.name);
      const parsed = (() => {
        const tmp = text.trim().split(/\r?\n/).filter(l => l.trim()).length;
        return tmp;
      })();
      toast.success(`${parsed} rows loaded from ${f.name}`);
    } catch (err) {
      toast.error("Failed to read file");
    }
  };

  const doPreview = async () => {
    try {
      const recips = (source === "csv" || source === "adhoc") ? parseCsv() : [];
      const r = await api.post("/whatsapp/preview", { template_body: body, recipients: recips, sample_size: 5 });
      setPreviews(r.data.previews || []);
      toast.success(`${r.data.total_input} recipients — showing first ${(r.data.previews||[]).length}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
  };

  const doSend = async () => {
    if (!body.trim()) return toast.error("Message body is empty");
    let parsed = [];
    if (source === "adhoc" || source === "csv") {
      parsed = parseCsv();
      if (!parsed.length) return toast.error("Add at least one recipient");
    }
    setSending(true);
    try {
      // Optionally persist to Contacts first (only for csv source since it has richer data)
      if (saveAsContacts && source === "csv" && parsed.length && activeId) {
        try {
          const contactsPayload = parsed.map(r => ({
            name: r.name || r.phone,
            phone: r.phone,
            email: r.email || "",
            company: r.company || "",
            address: r.address || "",
            gstin: r.gstin || "",
          }));
          const res = await api.post("/finance/contacts/bulk-import", {
            company_id: activeId,
            default_type: "client",
            rows: contactsPayload,
            update_existing: true,
          });
          toast.success(`Saved to Contacts · ${res.data.created} new · ${res.data.updated} updated · ${res.data.skipped_duplicate} dedup`);
        } catch (err) {
          toast.warning("Broadcast will still send · saving to Contacts failed: " + (err?.response?.data?.detail || err.message));
        }
      }
      const payload = {
        name: name || `Broadcast ${new Date().toLocaleString("en-IN", { day:"numeric", month:"short", hour:"2-digit", minute:"2-digit" })}`,
        template_id: templateId || "",
        template_body: body,
        media_url: mediaUrl,
        source,
        recipients: (source === "adhoc" || source === "csv") ? parsed : [],
        lead_status_filter: source === "leads" ? leadStatus : [],
        rate_per_minute: ratePerMinute,
        scheduled_for: scheduledFor ? new Date(scheduledFor).toISOString() : "",
      };
      const r = await api.post("/whatsapp/campaigns", payload);
      toast.success(`Broadcast queued · ${r.data.total_recipients} recipients`);
      setName(""); setCsvText(""); setCsvFileName(""); setPreviews([]);
      onSent && onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="wa-broadcast-form">
      <div className="lg:col-span-2 space-y-5">
        <Field label="Campaign name">
          <input data-testid="wa-name-input" value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. August Hot Leads Blast" className="w-full border border-black/15 px-3 py-2.5 text-sm" />
        </Field>

        <Field label="Template">
          <div className="flex gap-2 flex-wrap">
            <select data-testid="wa-template-select" value={templateId} onChange={e=>setTemplateId(e.target.value)} className="flex-1 min-w-[220px] border border-black/15 px-3 py-2.5 text-sm bg-white">
              <option value="">— Inline body (no template) —</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <span className="text-[10px] font-mono uppercase tracking-widest text-[#717680] self-center">or</span>
            <span className="self-center text-xs text-[#717680]">bhash default: <b>{config.default_template || "—"}</b></span>
          </div>
        </Field>

        <Field label="Message body" hint="Use {{name}} · {{company}} · {{amount}} for personalization">
          <textarea data-testid="wa-body-input" rows={5} value={body} onChange={e=>setBody(e.target.value)} placeholder="Hi {{name}}, quick heads-up — we have a special offer for {{company}} this week…" className="w-full border border-black/15 px-3 py-2.5 text-sm font-mono" />
          <div className="text-[10px] text-[#717680] mt-1">{body.length} chars</div>
        </Field>

        <Field label="Media URL (optional image / PDF)">
          <div className="flex items-center gap-2">
            <Paperclip size={14} className="text-[#717680]" />
            <input data-testid="wa-media-input" value={mediaUrl} onChange={e=>setMediaUrl(e.target.value)} placeholder="https://…" className="flex-1 border border-black/15 px-3 py-2 text-sm" />
          </div>
        </Field>

        <Field label="Recipient source">
          <div className="flex flex-wrap gap-2">
            {[
              { v: "adhoc", l: "Paste list" },
              { v: "csv", l: "CSV upload" },
              { v: "leads", l: "From Leads" },
              { v: "contacts", l: "From Contacts" },
            ].map(o => (
              <button key={o.v} data-testid={`wa-source-${o.v}`}
                onClick={() => setSource(o.v)}
                className={`px-3 py-2 text-xs font-semibold border transition ${source===o.v ? "bg-[#25D366] border-[#25D366] text-black" : "border-black/15 hover:bg-black/5"}`}>
                {o.l}
              </button>
            ))}
          </div>
        </Field>

        {source === "csv" && (
          <>
            <div className="border-2 border-dashed border-black/15 hover:border-[#25D366] p-6 text-center transition" data-testid="wa-csv-drop">
              <input ref={fileRef} type="file" accept=".csv,.txt" className="hidden" onChange={handleFile} data-testid="wa-csv-file-input" />
              <FileCsv size={28} weight="thin" className="mx-auto text-[#717680] mb-2" />
              <button data-testid="wa-csv-upload-btn" onClick={() => fileRef.current?.click()} className="text-sm font-semibold underline decoration-2 underline-offset-2 hover:text-[#25D366]">Choose CSV file</button>
              <div className="text-[11px] text-[#717680] mt-1.5">Auto-detects columns · phone, name, company, email, address, gstin</div>
              {csvFileName && <div className="mt-2 text-xs text-[#25D366] font-semibold" data-testid="wa-csv-filename">✓ {csvFileName} loaded</div>}
            </div>
            <Field label="Or paste directly">
              <textarea data-testid="wa-csv-input" rows={5} value={csvText} onChange={e=>{setCsvText(e.target.value); setCsvFileName("");}} placeholder={"phone,name,company\n9876543210,Rahul,ACME Hospital\n+91-9988776655,Priya,Fortis"} className="w-full border border-black/15 px-3 py-2.5 text-xs font-mono" />
            </Field>
            <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="wa-save-contacts-label">
              <input type="checkbox" checked={saveAsContacts} onChange={e=>setSaveAsContacts(e.target.checked)} data-testid="wa-save-contacts-checkbox" className="accent-[#25D366]" />
              <span>Also save these rows as Contacts (dedup by phone, updates existing)</span>
              {!activeId && <span className="text-[#D90429] text-[10px]">· pick a company first</span>}
            </label>
          </>
        )}
        {source === "adhoc" && (
          <Field label={`Recipients — one per line · phone,name,company`}>
            <textarea data-testid="wa-adhoc-input" rows={6} value={csvText} onChange={e=>setCsvText(e.target.value)} placeholder={"9876543210,Rahul,ACME Hospital\n+91-9988776655,Priya,Fortis"} className="w-full border border-black/15 px-3 py-2.5 text-xs font-mono" />
          </Field>
        )}
        {source === "leads" && (
          <Field label="Lead status filter">
            <div className="flex flex-wrap gap-1">
              {["hot", "warm", "cold", "new", "qualified"].map(s => (
                <button key={s} data-testid={`wa-lead-status-${s}`}
                  onClick={() => setLeadStatus(prev => prev.includes(s) ? prev.filter(x=>x!==s) : [...prev, s])}
                  className={`px-3 py-1.5 text-xs font-semibold uppercase border transition ${leadStatus.includes(s) ? "bg-black text-white border-black" : "border-black/15"}`}>
                  {s}
                </button>
              ))}
            </div>
          </Field>
        )}
        {source === "contacts" && (
          <div className="text-xs text-[#717680] italic p-3 bg-[#F5F6F8]">Sends to every contact with a phone — refine using the Contacts page filters first.</div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <Field label={`Rate limit · ${ratePerMinute}/min`}>
            <input type="range" min={10} max={300} step={10} value={ratePerMinute} onChange={e=>setRatePerMinute(+e.target.value)} data-testid="wa-rate-slider" className="w-full accent-[#25D366]" />
          </Field>
          <Field label="Schedule (optional)">
            <input type="datetime-local" data-testid="wa-schedule-input" value={scheduledFor} onChange={e=>setScheduledFor(e.target.value)} className="w-full border border-black/15 px-3 py-2 text-sm" />
          </Field>
        </div>

        <div className="flex gap-3 pt-3">
          <button data-testid="wa-preview-btn" onClick={doPreview} className="px-5 py-2.5 text-xs font-semibold uppercase tracking-wider border border-black/15 hover:bg-black hover:text-white transition">
            <Sparkle size={12} weight="bold" className="inline mr-1.5" /> Dry-run preview
          </button>
          <button data-testid="wa-send-btn" onClick={doSend} disabled={sending || !config.configured} className="px-5 py-2.5 text-xs font-bold uppercase tracking-wider bg-[#25D366] text-black hover:bg-black hover:text-[#25D366] transition disabled:opacity-40">
            <Broadcast size={12} weight="bold" className="inline mr-1.5" /> {sending ? "Queuing…" : scheduledFor ? "Schedule" : "Send Broadcast"}
          </button>
        </div>
      </div>

      <div className="lg:col-span-1 space-y-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#717680] font-bold">Preview</div>
        {(previews || []).length === 0 && <div className="text-xs text-[#717680] italic border border-dashed border-black/15 p-4">Click "Dry-run preview" to see how the message renders for the first 5 recipients.</div>}
        {(previews || []).map((p, i) => (
          <div key={i} className="border border-black/10 bg-white p-3" data-testid={`wa-preview-${i}`}>
            <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-[#717680] mb-2">
              <span>+91 · {p.phone}</span>
              {p.is_optout && <span className="text-[#D90429] font-bold">OPTED OUT</span>}
            </div>
            <div className="text-xs whitespace-pre-wrap font-mono text-[#0A0A0A]">{p.rendered}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// -------- Campaigns list --------
function CampaignsList({ campaigns, onRefresh, onOpen }) {
  const doAction = async (id, action) => {
    try {
      await api.patch(`/whatsapp/campaigns/${id}`, { action });
      toast.success(`${action}d`);
      onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };
  const doResendFailed = async (id) => {
    try {
      const r = await api.post(`/whatsapp/campaigns/${id}/resend-failed`);
      toast.success(`Retry queued · ${r.data.total_recipients} recipients`);
      onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Retry failed"); }
  };
  const badge = (s) => {
    const cmap = { queued: "#F59E0B", scheduled: "#3B82F6", sending: "#25D366", paused: "#717680", completed: "#0A0A0A", cancelled: "#D90429", failed: "#D90429" };
    return <span className="text-[10px] font-mono uppercase tracking-widest font-bold px-2 py-1" style={{ background: cmap[s]+"18", color: cmap[s] }}>{s}</span>;
  };
  return (
    <div data-testid="wa-campaigns-list">
      <div className="flex items-center justify-between mb-4">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#717680]">{campaigns.length} campaigns</div>
        <button data-testid="wa-refresh-list" onClick={onRefresh} className="text-xs border border-black/15 px-3 py-1.5 hover:bg-black hover:text-white transition inline-flex items-center gap-1.5"><ArrowClockwise size={12} weight="bold" /> Refresh</button>
      </div>
      {campaigns.length === 0 && <div className="text-xs text-[#717680] italic border border-dashed border-black/15 p-6 text-center">No campaigns yet. Head to <b>New Broadcast</b> to send your first blast.</div>}
      <div className="border border-black/10 bg-white divide-y divide-black/10">
        {campaigns.map(c => {
          const total = c.total_recipients || 0;
          const done = (c.sent_count || 0) + (c.failed_count || 0);
          const pct = total ? Math.round((done / total) * 100) : 0;
          const canResend = c.status === "completed" && (c.failed_count || 0) > 0;
          return (
            <div key={c.id} className="p-4 hover:bg-[#FAFBFC] cursor-pointer" data-testid={`wa-campaign-row-${c.id}`} onClick={() => onOpen(c.id)}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {badge(c.status)}
                    <div className="text-xs text-[#717680]">{new Date(c.created_at).toLocaleString("en-IN", { day:"numeric", month:"short", hour:"2-digit", minute:"2-digit" })}</div>
                    {c.resend_of && <span className="text-[9px] font-mono uppercase tracking-widest text-[#D90429] border border-[#D90429]/40 px-1.5 py-0.5">RETRY</span>}
                  </div>
                  <div className="font-semibold text-sm truncate">{c.name}</div>
                  <div className="text-[11px] text-[#717680] mt-1">{c.template_name || "inline"} · {total} recipients · {c.rate_per_minute}/min</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display font-black text-2xl leading-none tabular-nums">{c.sent_count || 0}<span className="text-sm text-[#717680]">/{total}</span></div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#717680] mt-1">{pct}% · {c.failed_count || 0} failed</div>
                </div>
              </div>
              <div className="mt-3 h-1.5 bg-black/5 overflow-hidden">
                <div className="h-full bg-[#25D366] transition-all" style={{ width: `${pct}%` }} />
              </div>
              <div className="mt-3 flex gap-2 flex-wrap" onClick={e=>e.stopPropagation()}>
                {["queued","sending","scheduled"].includes(c.status) && (
                  <button data-testid={`wa-pause-${c.id}`} onClick={()=>doAction(c.id,"pause")} className="text-[10px] font-semibold uppercase tracking-widest border border-black/15 px-2 py-1 hover:bg-black hover:text-white"><Pause size={10} weight="bold" className="inline mr-1" />Pause</button>
                )}
                {c.status === "paused" && (
                  <button data-testid={`wa-resume-${c.id}`} onClick={()=>doAction(c.id,"resume")} className="text-[10px] font-semibold uppercase tracking-widest border border-[#25D366] text-[#25D366] px-2 py-1 hover:bg-[#25D366] hover:text-black"><Play size={10} weight="bold" className="inline mr-1" />Resume</button>
                )}
                {c.status !== "completed" && c.status !== "cancelled" && (
                  <button data-testid={`wa-cancel-${c.id}`} onClick={()=>doAction(c.id,"cancel")} className="text-[10px] font-semibold uppercase tracking-widest border border-[#D90429]/40 text-[#D90429] px-2 py-1 hover:bg-[#D90429] hover:text-white"><Prohibit size={10} weight="bold" className="inline mr-1" />Cancel</button>
                )}
                {canResend && (
                  <button data-testid={`wa-retry-failed-${c.id}`} onClick={()=>doResendFailed(c.id)} className="text-[10px] font-semibold uppercase tracking-widest border border-[#D90429] bg-[#D90429] text-white px-2 py-1 hover:bg-black hover:border-black">
                    <ArrowClockwise size={10} weight="bold" className="inline mr-1" />Resend to {c.failed_count} failed
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// -------- Templates tab --------
function TemplatesTab({ templates, onChange }) {
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");
  const [tags, setTags] = useState("");

  const save = async () => {
    if (!name.trim() || !body.trim()) return toast.error("Name and body are required");
    try {
      await api.post("/whatsapp/templates", { name, body, media_url: mediaUrl, tags: tags.split(",").map(s=>s.trim()).filter(Boolean) });
      setName(""); setBody(""); setMediaUrl(""); setTags("");
      toast.success("Template saved");
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try { await api.delete(`/whatsapp/templates/${id}`); toast.success("Deleted"); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="wa-templates-tab">
      <div className="lg:col-span-2 space-y-4">
        <Field label="Template name"><input data-testid="wa-tpl-name" value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. New Product Announcement" className="w-full border border-black/15 px-3 py-2.5 text-sm" /></Field>
        <Field label="Body" hint="Use {{name}} · {{company}} · {{amount}}"><textarea data-testid="wa-tpl-body" rows={5} value={body} onChange={e=>setBody(e.target.value)} className="w-full border border-black/15 px-3 py-2.5 text-sm font-mono" /></Field>
        <Field label="Media URL (optional)"><input data-testid="wa-tpl-media" value={mediaUrl} onChange={e=>setMediaUrl(e.target.value)} placeholder="https://…" className="w-full border border-black/15 px-3 py-2 text-sm" /></Field>
        <Field label="Tags (comma-separated)"><input data-testid="wa-tpl-tags" value={tags} onChange={e=>setTags(e.target.value)} placeholder="promo, launch" className="w-full border border-black/15 px-3 py-2 text-sm" /></Field>
        <button data-testid="wa-tpl-save" onClick={save} className="px-5 py-2.5 text-xs font-bold uppercase tracking-wider bg-[#25D366] text-black hover:bg-black hover:text-[#25D366] transition">Save template</button>
      </div>
      <div className="lg:col-span-1 space-y-2">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#717680] font-bold">Saved · {templates.length}</div>
        {templates.map(t => (
          <div key={t.id} className="border border-black/10 bg-white p-3" data-testid={`wa-tpl-item-${t.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0"><div className="font-semibold text-sm truncate">{t.name}</div><div className="text-[10px] text-[#717680]">{(t.tags||[]).join(" · ") || "no tags"}</div></div>
              <button data-testid={`wa-tpl-del-${t.id}`} onClick={()=>del(t.id)} className="text-[#D90429] hover:bg-[#D90429] hover:text-white p-1"><Trash size={12} weight="bold" /></button>
            </div>
            <div className="mt-2 text-xs whitespace-pre-wrap font-mono text-[#4A4D57] max-h-24 overflow-auto">{t.body}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// -------- Deliveries tab --------
function DeliveriesTab({ campaigns, activeCampaignId, setActiveCampaignId }) {
  const [deliveries, setDeliveries] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const id = activeCampaignId || (campaigns[0] && campaigns[0].id);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const r = await api.get(`/whatsapp/campaigns/${id}/deliveries${statusFilter ? `?status=${statusFilter}` : ""}`);
      setDeliveries(r.data.deliveries || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, statusFilter]);

  const camp = campaigns.find(c => c.id === id);
  return (
    <div data-testid="wa-deliveries-tab">
      <div className="flex flex-wrap gap-3 items-center mb-4">
        <select data-testid="wa-deliv-campaign" value={id || ""} onChange={e=>setActiveCampaignId(e.target.value)} className="border border-black/15 px-3 py-2 text-sm bg-white">
          {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select data-testid="wa-deliv-status" value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} className="border border-black/15 px-3 py-2 text-sm bg-white">
          <option value="">All</option>
          <option value="queued">Queued</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
          <option value="optout">Opted out</option>
          <option value="held">Held (paused)</option>
        </select>
        <button data-testid="wa-deliv-refresh" onClick={load} className="text-xs border border-black/15 px-3 py-1.5 hover:bg-black hover:text-white inline-flex items-center gap-1.5"><ArrowClockwise size={12} weight="bold" /> Refresh</button>
        {camp && <div className="ml-auto text-xs text-[#717680]">Total <b>{camp.total_recipients}</b> · Sent <b className="text-[#25D366]">{camp.sent_count || 0}</b> · Failed <b className="text-[#D90429]">{camp.failed_count || 0}</b></div>}
      </div>
      {loading && <div className="text-xs text-[#717680]">Loading…</div>}
      {!loading && deliveries.length === 0 && <div className="text-xs text-[#717680] italic p-6 text-center border border-dashed border-black/15">No deliveries.</div>}
      <div className="border border-black/10 divide-y divide-black/10 bg-white max-h-[600px] overflow-auto">
        {deliveries.map(d => (
          <div key={d.id} className="p-3 flex items-start gap-3" data-testid={`wa-delivery-${d.id}`}>
            <StatusPill s={d.status} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs font-semibold">+91 · {d.phone}</span>
                <span className="text-xs text-[#717680]">· {d.name || "—"}</span>
                {d.sent_at && <span className="text-[10px] text-[#717680] ml-auto">{new Date(d.sent_at).toLocaleString("en-IN", { day:"numeric", month:"short", hour:"2-digit", minute:"2-digit" })}</span>}
              </div>
              <div className="text-xs text-[#4A4D57] whitespace-pre-wrap font-mono mt-1 line-clamp-2">{d.template_body_rendered}</div>
              {d.error_message && <div className="text-[10px] text-[#D90429] mt-1 font-mono">{d.error_message}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// -------- Opt-out tab --------
function OptoutTab() {
  const [rows, setRows] = useState([]);
  const [phone, setPhone] = useState("");
  const [reason, setReason] = useState("manual");
  const load = async () => {
    try { const r = await api.get("/whatsapp/optout"); setRows(r.data.optout || []); }
    catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!phone.trim()) return;
    try { await api.post("/whatsapp/optout", { phone, reason }); setPhone(""); toast.success("Added"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (p) => {
    try { await api.delete(`/whatsapp/optout/${p}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="max-w-2xl" data-testid="wa-optout-tab">
      <div className="mb-4 text-xs text-[#717680]">Phones on this list are automatically skipped in every future broadcast.</div>
      <div className="flex gap-2 mb-6">
        <input data-testid="wa-optout-phone" value={phone} onChange={e=>setPhone(e.target.value)} placeholder="9876543210" className="flex-1 border border-black/15 px-3 py-2 text-sm" />
        <input data-testid="wa-optout-reason" value={reason} onChange={e=>setReason(e.target.value)} placeholder="reason" className="w-40 border border-black/15 px-3 py-2 text-sm" />
        <button data-testid="wa-optout-add" onClick={add} className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-black text-white">Add</button>
      </div>
      <div className="border border-black/10 bg-white divide-y divide-black/10">
        {rows.length === 0 && <div className="p-4 text-xs text-[#717680] italic text-center">No opt-outs yet.</div>}
        {rows.map(r => (
          <div key={r.phone} className="p-3 flex items-center gap-3" data-testid={`wa-optout-row-${r.phone}`}>
            <span className="font-mono text-xs font-semibold flex-1">+91 · {r.phone}</span>
            <span className="text-xs text-[#717680]">{r.reason}</span>
            <button data-testid={`wa-optout-remove-${r.phone}`} onClick={()=>remove(r.phone)} className="text-[10px] font-semibold uppercase tracking-widest border border-[#D90429]/40 text-[#D90429] px-2 py-1 hover:bg-[#D90429] hover:text-white">Remove</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// -------- Little UI helpers --------
function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="font-mono text-[10px] uppercase tracking-widest text-[#0A0A0A] font-bold">{label}</span>
        {hint && <span className="text-[10px] text-[#717680] italic">{hint}</span>}
      </div>
      {children}
    </label>
  );
}
function StatusPill({ s }) {
  const cmap = { queued: "#F59E0B", sent: "#25D366", failed: "#D90429", optout: "#717680", held: "#3B82F6", cancelled: "#D90429" };
  return <span className="text-[9px] font-mono uppercase tracking-widest font-bold px-2 py-1 shrink-0 mt-0.5" style={{ background: cmap[s]+"20", color: cmap[s] }}>{s}</span>;
}
